// Advise Module Types — Supplier comparison and evaluation platform

// ============================================================
// Product Type Configuration (multi-product extensibility)
// ============================================================

export interface AdviseProductType {
  id: string;
  name: string;
  description: string;
  roiCalculators: RoiCalculatorType[];
}

export type RoiCalculatorType = 'baseline' | 'conversational_ai' | 'automated_qm' | 'real_time_assist';

// ============================================================
// Core Entities
// ============================================================

export interface AdviseCustomer {
  id: string;
  account_id: string;
  sf_customer_id?: string;
  sf_customer_name?: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  company_address: string;
  industry: string;
  company_size: string;
  status: string;
  budget_range: string;
  timeline: string;
  num_agents: number;
  priority_requirements: string[];
  notes: string;
  company_logo: string;
  created_date: string;
}

export interface AdviseSupplier {
  id: string;
  supplier_name: string;
  supplier_logo: string;
  sf_supplier_id?: string;
  sf_supplier_name?: string;
  product_category: string;
  capabilities: string[];
  supported_features: string[];
  headquarters: string;
  pricing_model: string;
  starting_price: number;
  strengths: string[];
  limitations: string[];
  custom_features: Record<string, string>;
  channel_manager_name: string;
  channel_manager_email: string;
  channel_manager_phone: string;
  created_date: string;
}

export interface AdviseEngagement {
  id: string;
  account_id: string;
  engagement_name: string;
  customer_id: string;
  product_type: string;
  selected_suppliers: string[];
  supplier_scores: Record<string, Record<string, number>>;
  feature_notes: Record<string, Record<string, string>>;
  uploaded_quotes: AdviseQuote[];
  line_item_mapping: Record<string, Record<string, string>>;
  telephony_usage_overrides: Record<string, { quantity: number; rate: number }>;
  custom_price_categories: string[];
  roi_analysis: Record<string, unknown>;
  data_room_files: AdviseDataRoomFile[];
  data_room_notes: string;
  data_room_note_files: AdviseDataRoomFile[];
  opportunity_sf_id: string | null;
  opportunity_name: string | null;
  shared_with: string[];
  share_token?: string;
  mutual_action_plan: AdviseMutualActionPlan;
  map_view_mode: string;
  map_columns: Record<string, string>;
  report_settings: Record<string, unknown>;
  imported_suppliers: string[];
  status: string;
  owner_name: string;
  created_by: string;
  created_date: string;
  // Only the show response carries it; false when the caller may read the engagement but not change it.
  can_edit?: boolean;
}

export interface EngagementListItem {
  id: string;
  account_id: string;
  customer_id: string | null;
  customer_name: string | null;
  engagement_name: string;
  product_type: string;
  status: string;
  owner_name: string | null;
  created_by: number | null;
  created_date: string;
  opportunity_sf_id: string | null;
  opportunity_name: string | null;
}

export interface AdvisoryOpportunity {
  sf_id: string;
  name: string;
  stage: string | null;
  close_date: string | null;
  product_interest: string | null;
  account_id: string;
}

export interface MyOpportunities {
  salesforce_user_found: boolean;
  opportunities: AdvisoryOpportunity[];
  new_opportunity_stage: string;
}

interface NewEngagementFields {
  account_id: string;
  engagement: { engagement_name: string; product_type: string; customer_id?: string };
}

export type NewEngagementRequest = NewEngagementFields &
  ({ opportunity_sf_id: string } | { new_opportunity: { name: string; close_date: string } });

export interface AdviseTask {
  id: string;
  account_id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  due_date: string;
  engagement_id?: string;
  created_by: string;
  updated_date: string;
}

// ============================================================
// Sub-types
// ============================================================

export interface AdviseQuote {
  file_url: string;
  file_name: string;
  supplier_name: string;
  extracted_pricing: {
    supplier: string;
    summary: string;
    line_items: AdviseLineItem[];
  };
}

export interface AdviseLineItem {
  item_name: string;
  unit_price: number;
  quantity: number;
  total_price: number;
  billing_cycle: 'monthly' | 'one-time';
}

export interface AdviseDataRoomFile {
  file_url: string;
  file_name: string;
  section?: string;
  uploaded_date: string;
}

export interface AdviseMutualActionPlan {
  milestones: AdviseMilestone[];
}

export interface AdviseMilestone {
  id: string;
  milestone: string;
  notes: string;
  status: 'not_started' | 'in_progress' | 'complete';
  due_date: string;
  owner: 'Seller' | 'Buyer' | 'Partner';
  created_date: string;
}

export interface EngagementStep {
  key: string;
  label: string;
  description: string | null;
  required: boolean;
  enabled: boolean;
}

export interface EngagementConfiguration {
  steps: EngagementStep[];
  can_configure?: boolean;
}

export type DiscoveryFieldType = 'text' | 'notes' | 'number' | 'select' | 'yes_no' | 'multi_select';

// The whole saved answer to one question; custom_choices are the options added with "+ Add", as typed.
export interface DiscoveryAnswer {
  answer?: string | number | boolean | string[] | null;
  notes?: string | null;
  custom_choices?: string[];
}

export interface DiscoveryQuestion {
  id: string;
  key: string;
  label: string;
  field_type: DiscoveryFieldType;
  required: boolean;
  group: string | null;
  placeholder: string | null;
  choices: string[] | null;
  default: string | boolean | null;
  has_notes: boolean;
  custom_choice_label: string | null;
  value: DiscoveryAnswer | null;
}

export interface DiscoveryFormSection {
  id: string;
  key: string;
  label: string;
  questions: DiscoveryQuestion[];
}

export interface MatrixFeature {
  id: string;
  // What a supplier's supported_features lists.
  key: string;
  label: string;
  custom: boolean;
  selected: boolean;
}

export interface MatrixCategory {
  id: string;
  key: string;
  label: string;
  custom: boolean;
  features: MatrixFeature[];
}

export interface MatrixCriterion {
  id: string;
  key: string;
  label: string;
  description: string | null;
  key_areas: string[];
  custom: boolean;
  selected: boolean;
  // A percentage in steps of 5; null while the criterion is cleared.
  weight: number | null;
  categories: MatrixCategory[];
}

export type MatrixCriterionValue = { selected: true; weight: number } | { selected: false };

export interface AdviseScoringResult {
  supplier_id: string;
  supplier_name: string;
  total_score: number;
  rank: number;
  criteria_scores: Record<string, number>;
  weighted_scores: Record<string, number>;
}

// ============================================================
// ROI Calculator Types
// ============================================================

export interface BaselineRoiInput {
  monthly_call_volume: number;
  number_of_agents: number;
  agent_annual_salary: number;
  cost_per_minute: number;
  supplier_licensing_cost: number;
  implementation_cost: number;
  aht_reduction_percent: number;
  auth_time_reduction_percent: number;
  chat_handle_time_reduction_percent: number;
  email_resolution_reduction_percent: number;
  chatbot_deflection_percent: number;
  conversion_improvement_percent: number;
  fte_reduction_percent: number;
  attrition_reduction_percent: number;
}

export interface BaselineRoiOutput {
  licensing_savings: number;
  aht_savings: number;
  auth_savings: number;
  chat_sms_savings: number;
  email_savings: number;
  chatbot_savings: number;
  total_labor_savings: number;
  total_cost_savings: number;
  revenue_uplift: number;
  op_efficiency: number;
  total_annual_benefit: number;
  net_benefit_year1: number;
  roi_percent: number;
  payback_months: number;
}

export interface ConversationalAiRoiInput {
  monthly_inbound: number;
  pct_eligible: number;
  containment_rate: number;
  aht_minutes: number;
  cost_per_minute: number;
  agent_cost_annual: number;
  annual_platform_cost: number;
  implementation_cost: number;
}

export interface ConversationalAiRoiOutput {
  automatable_monthly: number;
  ai_contained_monthly: number;
  savings_per_interaction: number;
  annual_hard_savings: number;
  agent_time_saved_hours_annual: number;
  labor_savings_annual: number;
  first_year_roi: number;
  payback_months: number;
  fte_equivalent: number;
}

export interface AutomatedQmRoiInput {
  monthly_interactions: number;
  percent_manual_qa: number;
  ai_eval_rate: number;
  ai_accuracy: number;
  current_analysts: number;
  analysts_required_with_ai: number;
  analyst_annual_cost: number;
  analyst_hourly_cost: number;
  monthly_time_saved: number;
  quality_improvement_value: number;
  annual_platform_cost: number;
  implementation_cost: number;
}

export interface AutomatedQmRoiOutput {
  manual_monthly_evals: number;
  analyst_reduction: number;
  annual_analyst_savings: number;
  annual_cost_savings: number;
  total_annual_benefit: number;
  net_benefit_year1: number;
  roi_pct: number;
}

export interface RealTimeAssistRoiInput {
  monthly_interactions: number;
  aht_minutes: number;
  cost_per_minute: number;
  num_agents: number;
  agent_annual_cost: number;
  aht_reduction_percent: number;
  ramp_time_reduction_percent: number;
  compliance_minutes_saved: number;
  compliance_improvement_percent: number;
  revenue_per_interaction: number;
  csat_improvement_percent: number;
  annual_platform_cost: number;
  implementation_cost: number;
}

export interface RealTimeAssistRoiOutput {
  baseline_labor_annual: number;
  aht_savings_annual: number;
  ramp_savings_annual: number;
  compliance_savings_annual: number;
  csat_revenue_impact: number;
  total_annual_benefit: number;
  net_benefit_year1: number;
  roi_pct: number;
}

// ============================================================
// Enums
// ============================================================

export const AdviseCustomerStatus = {
  DISCOVERY: 'discovery',
  REQUIREMENTS_GATHERING: 'requirements_gathering',
  SUPPLIER_EVALUATION: 'supplier_evaluation',
  PROPOSAL_STAGE: 'proposal_stage',
  NEGOTIATION: 'negotiation',
  CLOSED_WON: 'closed_won',
  CLOSED_LOST: 'closed_lost'
} as const;

export const AdviseCustomerIndustry = {
  TECHNOLOGY: 'Technology',
  HEALTHCARE: 'Healthcare',
  FINANCIAL_SERVICES: 'Financial Services',
  RETAIL: 'Retail',
  MANUFACTURING: 'Manufacturing',
  GOVERNMENT: 'Government',
  EDUCATION: 'Education',
  TELECOMMUNICATIONS: 'Telecommunications',
  ENERGY: 'Energy',
  MEDIA_ENTERTAINMENT: 'Media & Entertainment',
  OTHER: 'Other'
} as const;

export const AdviseCompanySize = {
  SMALL: '1-50',
  SMALL_MEDIUM: '51-200',
  MEDIUM: '201-1000',
  LARGE: '1001-5000',
  ENTERPRISE: '5000+'
} as const;

export const AdviseBudgetRange = {
  UNDER_50K: 'Under $50K',
  _50K_100K: '$50K - $100K',
  _100K_250K: '$100K - $250K',
  _250K_500K: '$250K - $500K',
  _500K_1M: '$500K - $1M',
  _1M_5M: '$1M - $5M',
  OVER_5M: 'Over $5M'
} as const;

export const EngagementStatus = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  PUBLISHED: 'published',
  IN_REVIEW: 'in_review'
} as const;

export type EngagementStatusValue = (typeof EngagementStatus)[keyof typeof EngagementStatus];

export const AdviseTaskStatus = {
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  DONE: 'done'
} as const;

export const AdviseTaskPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
} as const;

export const AdvisePricingModel = {
  PER_AGENT_MONTHLY: 'per_agent_monthly',
  PER_INTERACTION: 'per_interaction',
  TIERED_PRICING: 'tiered_pricing',
  ENTERPRISE_CUSTOM: 'enterprise_custom',
  USAGE_BASED: 'usage_based'
} as const;

export const AdviseMilestoneOwner = {
  SELLER: 'Seller',
  BUYER: 'Buyer',
  PARTNER: 'Partner'
} as const;

export const DEFAULT_PRICE_CATEGORIES = [
  'Licensing',
  'Telco / Usage',
  'Professional Services',
  'WFM',
  'Quality Management',
  'AI & Automation',
  'Analytics',
  'Recording & Storage',
  'Integration',
  'Support & Maintenance',
  'Other'
] as const;
